import { useState, useEffect } from 'react'
import { Building2, Users, MessageSquare, Database, TrendingUp, AlertCircle } from 'lucide-react'
import { getApiBase, getToken } from '../lib/utils'
import { useAppLanguage } from '../i18n'

interface WorkspaceMetric {
  id: number
  name: string
  slug: string
  deployment_mode: string
  created_at: string
  metrics: {
    agents: number
    conversations: number
    knowledge_items: number
    storage_mb: number
  }
}

interface GlobalUsage {
  agents: { current: number; limit: number; percentage: number }
  conversations: { current: number; limit: number; percentage: number }
  knowledge_items: { current: number; limit: number; percentage: number }
  storage: { current_mb: number; limit_mb: number; percentage: number }
}

interface DashboardData {
  is_agency: boolean
  workspaces: WorkspaceMetric[]
  workspace_count: number
  global_usage: GlobalUsage
  limits: any
  plan_info: {
    name: string
    slug: string
    is_agency: boolean
  }
}

export default function AgencyDashboardPage() {
  const { t } = useAppLanguage()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDashboard()
  }, [])

  const fetchDashboard = async () => {
    try {
      const token = getToken()
      if (!token) {
        setError('Vous devez être connecté')
        setLoading(false)
        return
      }

      const response = await fetch(`${getApiBase()}/agency/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erreur lors du chargement du dashboard')
      }

      const dashboardData = await response.json()
      setData(dashboardData)
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500'
    if (percentage >= 75) return 'bg-orange-500'
    if (percentage >= 50) return 'bg-yellow-500'
    return 'bg-emerald-500'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-zinc-400">{t.agencyDashboard.loading}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20 max-w-md">
          <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-400">{t.agencyDashboard.error}</p>
            <p className="text-sm text-red-300 mt-1">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!data) {
    return null
  }

  // Si pas Agency, afficher un message
  if (!data.is_agency) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="p-8 rounded-2xl bg-zinc-900 border border-zinc-800">
          <div className="text-center">
            <Building2 className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-zinc-100 mb-2">{t.agencyDashboard.title}</h2>
            <p className="text-zinc-400 mb-6">
              {t.agencyDashboard.notAgency}
            </p>
            <a
              href="/pricing"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-all"
            >
              {t.agencyDashboard.upgradeButton}
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-100">{t.agencyDashboard.title}</h1>
          <p className="text-zinc-400 mt-1">{t.agencyDashboard.subtitle} {data.workspace_count} {data.workspace_count > 1 ? t.agencyDashboard.workspaces : t.agencyDashboard.workspace}</p>
        </div>
        <div className="px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <span className="text-sm font-semibold text-emerald-400">
            {t.agencyDashboard.plan} {data.plan_info.name}
          </span>
        </div>
      </div>

      {/* Global Usage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Agents */}
        <div className="p-6 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <Users className="h-8 w-8 text-blue-400" />
            <span className="text-2xl font-bold text-zinc-100">
              {data.global_usage.agents.current} <span className="text-sm text-zinc-500">/ {data.global_usage.agents.limit}</span>
            </span>
          </div>
          <h3 className="text-sm font-medium text-zinc-400 mb-2">{t.agencyDashboard.globalMetrics.agents}</h3>
          <div className="w-full bg-zinc-800 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all ${getProgressColor(data.global_usage.agents.percentage)}`}
              style={{ width: `${Math.min(data.global_usage.agents.percentage, 100)}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500">
            {data.global_usage.agents.limit === -1 ? t.agencyDashboard.unlimited : `${data.global_usage.agents.current} / ${data.global_usage.agents.limit}`}
          </p>
        </div>

        {/* Conversations */}
        <div className="p-6 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <MessageSquare className="h-8 w-8 text-emerald-400" />
            <span className="text-2xl font-bold text-zinc-100">
              {data.global_usage.conversations.current} <span className="text-sm text-zinc-500">/ {data.global_usage.conversations.limit}</span>
            </span>
          </div>
          <h3 className="text-sm font-medium text-zinc-400 mb-2">{t.agencyDashboard.globalMetrics.conversations}</h3>
          <div className="w-full bg-zinc-800 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all ${getProgressColor(data.global_usage.conversations.percentage)}`}
              style={{ width: `${Math.min(data.global_usage.conversations.percentage, 100)}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500">
            {data.global_usage.conversations.limit === -1 ? t.agencyDashboard.unlimited : `${data.global_usage.conversations.current} / ${data.global_usage.conversations.limit} ${t.agencyDashboard.thisMonth}`}
          </p>
        </div>

        {/* Knowledge Items */}
        <div className="p-6 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <Database className="h-8 w-8 text-purple-400" />
            <span className="text-2xl font-bold text-zinc-100">
              {data.global_usage.knowledge_items.current} <span className="text-sm text-zinc-500">/ {data.global_usage.knowledge_items.limit}</span>
            </span>
          </div>
          <h3 className="text-sm font-medium text-zinc-400 mb-2">{t.agencyDashboard.globalMetrics.knowledge}</h3>
          <div className="w-full bg-zinc-800 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all ${getProgressColor(data.global_usage.knowledge_items.percentage)}`}
              style={{ width: `${Math.min(data.global_usage.knowledge_items.percentage, 100)}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500">
            {data.global_usage.knowledge_items.limit === -1 ? t.agencyDashboard.unlimited : `${data.global_usage.knowledge_items.current} / ${data.global_usage.knowledge_items.limit} ${t.agencyDashboard.items}`}
          </p>
        </div>

        {/* Storage */}
        <div className="p-6 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="h-8 w-8 text-orange-400" />
            <span className="text-2xl font-bold text-zinc-100">
              {data.global_usage.storage.current_mb.toFixed(1)} MB
            </span>
          </div>
          <h3 className="text-sm font-medium text-zinc-400 mb-2">{t.agencyDashboard.globalMetrics.storage}</h3>
          <div className="w-full bg-zinc-800 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all ${getProgressColor(data.global_usage.storage.percentage)}`}
              style={{ width: `${Math.min(data.global_usage.storage.percentage, 100)}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500">
            {data.global_usage.storage.limit_mb === -1 ? t.agencyDashboard.unlimited : `${data.global_usage.storage.current_mb.toFixed(1)} MB / ${data.global_usage.storage.limit_mb} MB`}
          </p>
        </div>
      </div>

      {/* Info Box - Quotas Poolés */}
      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <p className="text-sm text-blue-300">
          <strong>{t.agencyDashboard.pooledQuotas} :</strong> {t.agencyDashboard.pooledQuotasDescription}
        </p>
      </div>

      {/* Workspaces List */}
      <div>
        <h2 className="text-xl font-bold text-zinc-100 mb-4">{t.agencyDashboard.workspacesList.title}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.workspaces.map((workspace) => (
            <div key={workspace.id} className="p-6 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-100">{workspace.name}</h3>
                    <p className="text-xs text-zinc-500">
                      {workspace.deployment_mode === 'production' ? t.agencyDashboard.production : t.agencyDashboard.preview}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-500">{t.agencyDashboard.globalMetrics.agents}</p>
                  <p className="text-lg font-semibold text-zinc-200">{workspace.metrics.agents}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">{t.agencyDashboard.globalMetrics.conversations}</p>
                  <p className="text-lg font-semibold text-zinc-200">{workspace.metrics.conversations}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">{t.agencyDashboard.globalMetrics.knowledge}</p>
                  <p className="text-lg font-semibold text-zinc-200">{workspace.metrics.knowledge_items}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">{t.agencyDashboard.globalMetrics.storage}</p>
                  <p className="text-lg font-semibold text-zinc-200">{workspace.metrics.storage_mb.toFixed(1)} MB</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
